<template>
  <div class="welcome" @pointerdown="prime">
    <div class="panel">
      <p class="kicker">{{ t('welcome.kicker') }}</p>
      <h2>{{ t('welcome.title') }}</h2>
      <p class="lead">{{ t('welcome.lead') }}</p>
      <div class="thumbs">
        <div v-for="pack in gifts" :key="pack.id" class="thumb">
          <PackCover :src="pack.cover" :alt="pack.name" size="sm" />
          <span>{{ t(`packs.tagline.${pack.id}`) }}</span>
          <span class="qty">×{{ pack.count }}</span>
        </div>
      </div>
      <button type="button" class="cta" @click="$emit('continue')">
        {{ t('welcome.cta') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { starterPackCounts } from '@shared/index'
import { PACKS } from '../game/packs'
import { DuelSfx } from '../game/audio/DuelSfx'
import PackCover from './PackCover.vue'
import { useI18n } from '../i18n/t'

defineEmits<{ continue: [] }>()

const { t } = useI18n()

const giftCounts = starterPackCounts()
const gifts = PACKS.filter((pack) => (giftCounts[pack.id] ?? 0) > 0).map(
  (pack) => ({ ...pack, count: giftCounts[pack.id] ?? 0 }),
)
const prime = () => DuelSfx.prime()
</script>

<style scoped>
.welcome {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  background: var(--md-scrim);
  backdrop-filter: blur(10px);
}

.panel {
  width: min(560px, 100%);
  max-height: 100%;
  overflow-y: auto;
  padding: 32px 28px;
  border-radius: 12px;
  background: var(--md-sys-color-surface-container);
  border: 1px solid color-mix(in srgb, var(--md-sys-color-primary) 40%, transparent);
  box-shadow: var(--md-elevation-2);
  color: var(--md-sys-color-on-surface);
  text-align: center;
}

.kicker {
  color: var(--md-sys-color-primary);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 11px;
  margin-bottom: 8px;
}

h2 {
  font-size: 28px;
  color: var(--md-sys-color-on-surface);
  margin-bottom: 12px;
}

.lead {
  color: var(--md-sys-color-on-surface-variant);
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: 22px;
}

.thumbs {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.thumb {
  width: min(110px, 28vw);
}

.thumb :deep(.booster) {
  display: block;
  margin: 0 auto;
}

.thumb span {
  display: block;
  margin-top: 6px;
  font-size: 11px;
  color: var(--md-sys-color-on-surface-variant);
}

.thumb .qty {
  margin-top: 2px;
  font-weight: 700;
  color: var(--md-sys-color-primary);
}

.cta {
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 10px;
  background: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
  font-weight: 700;
  cursor: pointer;
}

.cta:hover {
  filter: brightness(1.08);
}

@media (max-height: 500px) and (max-width: 1100px) {
  .panel {
    padding: 16px 20px;
  }

  h2 {
    font-size: 22px;
    margin-bottom: 8px;
  }

  .lead {
    margin-bottom: 12px;
    font-size: 14px;
  }

  .thumbs {
    flex-wrap: nowrap;
    margin-bottom: 12px;
    overflow-x: auto;
  }

  .thumb {
    width: min(88px, 22vw);
    flex-shrink: 0;
  }
}
</style>
